// src/common/hooks/useNetworkMonitor.ts
import { useEffect, useCallback, useState } from "react";
import useInterval from "@use-it/interval";
import { getApi } from "../../api/api";
import { store } from "../../store/store";
import { setOnline } from "../../store/slices/networkSlice";
import { isRuntimeConfigLoaded } from "../../config/runtimeConfig";

const ONLINE_POLL_INTERVAL = 3000;
const OFFLINE_POLL_INTERVAL = 5000;

export const useNetworkMonitor = (configReady: boolean) => {
  const [api, setApi] = useState<ReturnType<typeof getApi> | null>(null);

  useEffect(() => {
    if (configReady && isRuntimeConfigLoaded() && !api) {
      setApi(getApi());
    }
  }, [configReady, api]);

  const checkNetwork = useCallback(async () => {
    if (!api) return;

    try {
      await api.healthCheck(2000);
      const currentStatus = store.getState().network.isOnline;
      if (!currentStatus) {
        store.dispatch(setOnline(true));
      }
    } catch (error) {
      const currentStatus = store.getState().network.isOnline;
      if (currentStatus) {
        store.dispatch(setOnline(false));
      }
    }
  }, [api]);

  useEffect(() => {
    if (api) {
      if (navigator.onLine) {
        checkNetwork();
      } else {
        store.dispatch(setOnline(false));
      }
    }
  }, [api, checkNetwork]);

  useInterval(
    () => {
      if (api) checkNetwork();
    },
    store.getState().network.isOnline
      ? ONLINE_POLL_INTERVAL
      : OFFLINE_POLL_INTERVAL,
  );

  useEffect(() => {
    if (!api) return;

    const handleOnline = () => checkNetwork();
    const handleOffline = () => store.dispatch(setOnline(false));

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [api, checkNetwork]);

  return null;
};